import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import HelpIcon from "@mui/icons-material/Help";
import React, { useMemo, useState } from "react";
import {
  useDeleteExternalAction,
  useGetAllExternalActions,
  useRejectExternalAction,
  useRunExternalAction,
} from "./hooks";

import { ActionData } from "./types";
import ActionToolbar from "./ActionToolbar";
import ActionsTable from "./ActionsTable";
import JsonModal from "./JsonModal";
import { PageLayout } from "src/reusable/components/DashboardComponents";
import StatisticsCards from "./StatisticsCards";
import { useParams } from "react-router-dom";
import { useHelpDrawer } from "src/components/help";
import { HelpFlag, isHelpFeatureEnabled } from "src/config/posthog";

// Define the type for route parameters
interface ActionParams {
  action_id?: string;
}

export const AtomAssistActions = () => {
  // State for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalTitle, setModalTitle] = useState("");
  const { openDrawer } = useHelpDrawer();
  const helpEnabled = isHelpFeatureEnabled(HelpFlag.HELP_DRAWER);

  // State for filtering and refresh
  const [lastRefreshed, setLastRefreshed] = useState(new Date());

  // State for pagination
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  // State for sorting
  const [sortField, setSortField] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");

  // React Query hooks
  const { data: actions = [], isLoading, refetch } = useGetAllExternalActions();
  const deleteActionMutation = useDeleteExternalAction();
  const runActionMutation = useRunExternalAction();
  const rejectActionMutation = useRejectExternalAction();

  const params = useParams<ActionParams>();
  const actionIdNumber = params.action_id
    ? parseInt(params.action_id, 10)
    : null;

  // Function to handle refresh
  const handleRefresh = () => {
    refetch();
    setLastRefreshed(new Date());
  };

  // Function to open modal with data
  const handleOpenModal = (data: any, title: string) => {
    setModalData(data);
    setModalTitle(title);
    setModalOpen(true);
  };

  // Function to close modal
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Function to handle sort changes
  const handleSortChange = (field: keyof ActionData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Function to handle page change
  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleApprove = async (id: number) => {
    try {
      await runActionMutation.mutateAsync(id);
      handleRefresh();
    } catch (err) {
      console.error("Approval failed", err);
    }
  };

  const handleReject = async (id: number) => {
    try {
      await rejectActionMutation.mutateAsync(id);
      handleRefresh();
    } catch (err) {
      console.error("Rejection failed", err);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteActionMutation.mutateAsync(id);
      handleRefresh();
    } catch (err) {
      console.error("Delete failed", err);
    }
  };

  // Function to extract action type name from full path
  const getActionTypeName = (fullPath: string) => {
    if (!fullPath) return "-";
    const parts = fullPath.split(".");
    return parts[parts.length - 1] || fullPath;
  };

  // Process and sort data
  const processedData = useMemo(() => {
    if (!actions || actions.length === 0) return [];

    let result = [...actions];

    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle special cases
      if (sortField === "on_approval_action") {
        aValue = getActionTypeName(a.on_approval_action);
        bValue = getActionTypeName(b.on_approval_action);
      } else if (sortField === "created_by") {
        aValue = a.created_by?.user_display_name || "";
        bValue = b.created_by?.user_display_name || "";
      } else if (sortField === "approved_by") {
        aValue = a.approved_by?.user_display_name || "";
        bValue = b.approved_by?.user_display_name || "";
      } else if (sortField === "create_date" || sortField === "updated_date") {
        aValue = new Date(a[sortField] || 0).getTime();
        bValue = new Date(b[sortField] || 0).getTime();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    // Apply pagination
    const startIndex = (page - 1) * rowsPerPage;
    return result.slice(startIndex, startIndex + rowsPerPage);
  }, [actions, page, rowsPerPage, sortField, sortDirection]);

  // Calculate total pages
  const totalPages = useMemo(() => {
    if (!actions) return 0;
    return Math.ceil(actions.length / rowsPerPage);
  }, [actions, rowsPerPage]);

  return (
    <PageLayout>
      {/* Header */}
      <Box sx={{ mb: 4 }} data-testid="external-actions-page">
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight="bold"
              color="gray"
              mb={0.5}
              data-testid="external-actions-heading"
            >
              External Actions
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" mb={3}>
              View and manage external actions staged by Atom Assist.
            </Typography>
          </Box>
          {helpEnabled && (
            <Tooltip title="How to use Actions">
              <IconButton
                data-testid="aa-act-btn-user-guide"
                data-attr="assistant-actions:btn:open-user-guide"
                onClick={() =>
                  openDrawer({
                    entryPoint: "page_top_button",
                    product: "agents",
                  })
                }
                color="primary"
                size="small"
              >
                <HelpIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Statistics Cards Component */}
        <StatisticsCards actions={actions} isLoading={isLoading} />
      </Box>

      {/* Toolbar Component */}
      <ActionToolbar
        lastRefreshed={lastRefreshed}
        handleRefresh={handleRefresh}
      />

      {/* Table Component */}
      <ActionsTable
        isLoading={isLoading}
        processedData={processedData}
        actionIdNumber={actionIdNumber}
        sortField={sortField}
        sortDirection={sortDirection}
        handleSortChange={handleSortChange}
        handleOpenModal={handleOpenModal}
        handleApprove={handleApprove}
        handleReject={handleReject}
        handleDelete={handleDelete}
        getActionTypeName={getActionTypeName}
        page={page}
        totalPages={totalPages}
        handlePageChange={handlePageChange}
        actions={actions}
      />

      {/* JSON Modal */}
      <JsonModal
        open={modalOpen}
        onClose={handleCloseModal}
        data={modalData}
        title={modalTitle}
      />
    </PageLayout>
  );
};

export default AtomAssistActions;
